import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { createBaseQuery } from '../helper/createBaseQuery'
import { validateQueryStringParameters } from '../../helpers/api'
import { tokenSlice } from '../slice/tokenSlice'

const paymentApi = tokenSlice.injectEndpoints({
  reducerPath: 'payment',
  baseQuery: createBaseQuery(),
  tagTypes: ['Payment'],
  endpoints(builder) {
    return {
      // GET
      getWaitingPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/WaitingForApproval`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getAllPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/All`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getDraftPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/Draft`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getApprovedPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/Approved`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getHeldPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/Held`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getOrdersPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/PaymentOrders`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getRejectedPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/Rejected`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getBankPayment: builder.query({
        query: (params) => {
          return {
            url: `Payment/Bank`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getBalancePayment: builder.query({
        query: (params) => {
          const { vendorCode, businessUnitId, type } = params
          return {
            url: `Payment/Balance?vendorCode=${!vendorCode ? 0 : vendorCode
              }&businessUnitId=${businessUnitId}&type=${type}`,
            method: `GET`,
          }
        },
        providesTags: ['Payment'],
      }),

      getPaymentAdvance: builder.query({
        query: (params) => {
          if (!params) {
            return
          }

          return {
            url: `Payment/Advance`,
            method: `GET`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getPaymentOrder: builder.query({
        query: (params) => {
          if (!params) {
            return
          }

          return {
            url: `Payment/Order`,
            method: `GET`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getPaymentCreateDocument: builder.query({
        query: (params) => {
          if (!params) {
            return
          }

          return {
            url: `Payment/CreateDocument`,
            method: `GET`,
            params: validateQueryStringParameters(params),
          }
        },
        providesTags: ['Payment'],
      }),

      getPaymentInfoId: builder.query({
        query: (id) => {
          return {
            url: `Payment/Info/${id}`,
            method: `GET`,
          }
        },
        providesTags: ['Payment'],
      }),

      postPaymentSendToApprove: builder.mutation({
        query: (data) => ({
          url: `Payment/SendToApprove`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      postPaymentChangeStatus: builder.mutation({
        query: (data) => ({
          url: 'Payment/ChangeStatus',
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      getPaymentVendorBalance: builder.query({
        query: (params) => {
          return {
            url: `Payment/VendorBalance/${params?.businessUnitId}/${params?.vendorCode}`,
            method: `GET`,
          }
        },
        providesTags: ['Payment'],
      }),

      // getPaymentPaymentRequest: builder.query({
      //   query: (params) => {
      //     if (!params) return
      //     const { vendorCode, currencyCode } = params

      //     return {
      //       url: `Payment/PaymentRequest?vendorCode=${vendorCode}&currencyCode=${currencyCode}`,
      //       method: `GET`,
      //     }
      //   },
      //   providesTags: ['Payment'],
      // }),

      getPaymentPaymentRequest: builder.query({
        query: (args) => {
          const { vendorCode, currencyCode, businessUnitId } = args;
          return {
            url: `Payment/PaymentRequest?vendorCode=${vendorCode}&currencyCode=${currencyCode}&businessUnitId=${businessUnitId}`,
          };
        },
        providesTags: ["User"],
      }),

      postPaymentSave: builder.mutation({
        query: (data) => ({
          url: `Payment/Save`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      postPaymentRetrieve: builder.mutation({
        query: (data) => ({
          url: `Payment/Retrieve`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      postPaymentReturnToApproved: builder.mutation({
        query: (data) => ({
          url: `Payment/ReturnToApproved`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      postPaymentSendToBank: builder.mutation({
        query: (data) => ({
          url: `Payment/SendToBank`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      postPaymentChangesPaymentBankCharge: builder.mutation({
        query: (data) => ({
          url: `Payment/ChangesPaymentBankCharge`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      paymentOrderLoad: builder.mutation({
        query: (data) => ({
          url: `Payment/PaymentOrderLoad`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      paymentOrderTransaction: builder.mutation({
        query: (data) => ({
          url: `Payment/PaymentOrderTransaction`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Payment'],
      }),

      deletePayment: builder.mutation({
        query: (data) => {
          return {
            url: `Payment/Delete`,
            method: 'POST',
            body: data,
          }
        },
        invalidatesTags: ['Payment'],
      }),

      getPaymentAttachments: builder.query({
        query: (params) => {
          return {
            url: `Attachment/GetPaymentAttachments`,
            params: validateQueryStringParameters(params),
          }
        },

        providesTags: ['Payment'],
      }),

      getBankAccountList: builder.query({
        query: (params) => {
          return {
            url: `Payment/BankAccountList`,
            params: validateQueryStringParameters(params),
          }
        },

        providesTags: ['Payment'],
      }),

      postPaymentOrderPost: builder.mutation({
        query: (data) => {
          return {
            url: `Payment/PaymentOrderPostData`,
            method: 'POST',
            body: data,
          }
        },
        invalidatesTags: ['Payment'],
      }),
    }
  },
})

export const {
  useGetWaitingPaymentQuery,
  useGetAllPaymentQuery,
  useGetDraftPaymentQuery,
  useGetApprovedPaymentQuery,
  useGetHeldPaymentQuery,
  useGetOrdersPaymentQuery,
  useGetRejectedPaymentQuery,
  useGetBankPaymentQuery,
  useGetBalancePaymentQuery,
  useGetPaymentAdvanceQuery,
  useGetPaymentOrderQuery,
  useGetPaymentCreateDocumentQuery,
  useGetPaymentInfoIdQuery,
  usePostPaymentSendToApproveMutation,
  usePostPaymentChangeStatusMutation,
  useGetPaymentVendorBalanceQuery,
  useGetPaymentPaymentRequestQuery,
  usePostPaymentSaveMutation,
  usePostPaymentRetrieveMutation,
  usePostPaymentReturnToApprovedMutation,
  usePostPaymentSendToBankMutation,
  usePostPaymentChangesPaymentBankChargeMutation,
  usePostPaymentPayMutation,
  useDeletePaymentMutation,
  usePaymentOrderLoadMutation,
  usePaymentOrderTransactionMutation,
  useGetPaymentAttachmentsQuery,
  useGetBankAccountListQuery,
  usePostPaymentOrderPostMutation,
} = paymentApi

export { paymentApi }
